import { Play, Radio, Tv } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePublicProgrammes } from '../lib/api/programmes';
import { useYouTubeLive } from '../lib/api/youtube';
import type { PublicProgrammeListItem } from '../lib/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Spinner } from './ui/spinner';

interface LiveNowStripProps {
  locale: string;
  /** Hide the card chrome and just render the inline strip — useful inside other cards. */
  inline?: boolean;
  /** Cap on programmes polled. Default 20 — covers most rosters; bump for bigger ones. */
  limit?: number;
}

/**
 * Lists programmes that are currently `live` or `upcoming` on YouTube. Polls each
 * programme's `/youtube/live` endpoint every 30s — fine because the API caches
 * under the hood. We don't bother with batching: with ~10 active programmes and a
 * 30s cache, this is well under quota.
 */
export function LiveNowStrip({ locale, inline = false, limit = 20 }: LiveNowStripProps) {
  const programmes = usePublicProgrammes({ locale, page: 1, pageSize: limit });
  const withYouTube = (programmes.data?.items ?? []).filter(
    (p) => p.youtubeChannelId || p.youtubeHandle || p.youtubeLiveStreamId,
  );

  if (programmes.isLoading) {
    return inline ? <Spinner label="Loading live status…" /> : null;
  }
  if (withYouTube.length === 0) {
    if (inline) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle>Live now</CardTitle>
          <CardDescription>
            No programmes are configured with YouTube channels yet. Add one in the YouTube tab of
            any programme.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const inner = (
    <div className="grid gap-2">
      {withYouTube.map((p) => (
        <ProgrammeLiveRow key={p.id} programme={p} />
      ))}
    </div>
  );

  if (inline) return inner;

  return (
    <Card>
      <CardHeader>
        <p className="eyebrow">
          <span className="live-dot" /> On Air
        </p>
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-brand" />
          <CardTitle>Live now & upcoming</CardTitle>
        </div>
        <CardDescription>
          Polling YouTube every 30s — watching {withYouTube.length}{' '}
          programme{withYouTube.length === 1 ? '' : 's'}.
        </CardDescription>
      </CardHeader>
      <CardContent>{inner}</CardContent>
    </Card>
  );
}

/** Single row in the live-now strip. Hidden when the programme isn't live or upcoming
 *  — keeps the strip tight rather than showing every "off-air" programme. */
function ProgrammeLiveRow({ programme }: { programme: PublicProgrammeListItem }) {
  const { data } = useYouTubeLive(programme.id);
  const [videoOpen, setVideoOpen] = useState(false);

  if (!data || !data.configured || data.state === 'none') return null;

  const isLive = data.state === 'live';
  const embedId = data.videoId ?? programme.youtubeLiveStreamId;
  const subtitle = isLive
    ? data.concurrentViewers
      ? `${data.concurrentViewers.toLocaleString()} watching`
      : 'On air'
    : data.scheduledStartTime
      ? `Starts ${new Date(data.scheduledStartTime).toLocaleString()}`
      : 'Upcoming';

  return (
    <>
      <div className="group flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2.5 text-sm transition-colors hover:border-line-strong hover:bg-surface-tinted">
        <span
          className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            isLive ? 'bg-live text-white shadow-sm' : 'bg-warning/10 text-warning'
          }`}
        >
          {isLive ? <Radio className="h-4 w-4" /> : <Tv className="h-4 w-4" />}
        </span>

        <Link to={`/programmes/${programme.slug}`} className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-medium leading-tight text-ink group-hover:text-brand">
            {programme.title}{' '}
            {data.title && data.title !== programme.title ? (
              <span className="font-normal text-ink-subtle"> — {data.title}</span>
            ) : null}
          </p>
          <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-subtle">
            <span className="normal-case tracking-normal">{subtitle}</span>
          </p>
        </Link>

        {/* Watch button — only when live and we have an embeddable video ID */}
        {isLive && embedId ? (
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            title="Watch live stream"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-live/10 text-live transition-colors hover:bg-live hover:text-white"
          >
            <Play className="h-3.5 w-3.5 translate-x-px" />
          </button>
        ) : null}

        <span
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
            isLive ? 'bg-live/10 text-live' : 'bg-warning/10 text-warning'
          }`}
        >
          {isLive ? <span className="live-dot" /> : null}
          {isLive ? 'Live' : 'Soon'}
        </span>
      </div>

      {isLive && embedId ? (
        <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
          <DialogContent size="xl" className="overflow-hidden p-0">
            <DialogHeader className="px-5 pb-3 pt-5">
              <DialogTitle>{programme.title} — Live</DialogTitle>
              {data.title && data.title !== programme.title ? (
                <p className="text-sm text-ink-muted">{data.title}</p>
              ) : null}
            </DialogHeader>
            <div className="relative w-full bg-black pb-[56.25%]">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${embedId}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${programme.title} live stream`}
                className="absolute inset-0 h-full w-full border-0"
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
